<script setup lang="ts">
import type { Registration, SleevesPerReg } from '~/types'
import { ref } from 'vue'
import { useTabSessionStore } from '~/stores/tabSession'

const props = defineProps<{
  selectedHousehold: object | undefined
  registrations: Registration[] | undefined
  sleeves: SleevesPerReg[] | undefined
  householdName: string
  householdRepName: string
  /** True while useSidebarData is fetching. Swaps the account list for its
   *  skeleton; a warm sessionStorage cache never sets this, so a returning
   *  user goes straight to real rows. */
  loading?: boolean
}>()

// householdName = selectedHousehold?.label[0]

const emit = defineEmits<{
  (e: 'selectHousehold'): void
}>()

function onSelectHousehold() {
  emit('selectHousehold')
}

const open = ref(false)

const tabSession = useTabSessionStore()

/**
 * Clear all selected sidebar state:
 * - selected registration
 * - selected sleeve
 * - expanded registration
 *
 * This removes highlight/selection from NavRegsAndSleevesList
 */
function clearSelectedRegAndSleeve() {
  tabSession.setRegistrationId(null)
  tabSession.setRegistrationObj(null)

  tabSession.setSleeveId(null)
  tabSession.setSleeveObj(null)

  tabSession.setExpandedRegistrationId(null)
}

/**
 * Click handler for Advisor button
 */
function onAdvisorClick() {
  clearSelectedRegAndSleeve()
}

/**
 * Click handler for Household button
 * Also triggers your existing household selection event
 */
function onHouseholdClick() {
  clearSelectedRegAndSleeve()
  onSelectHousehold()
}

// Prop: open, collapsed, selectedHousehold, registrations,
// Event: selectRegistration, expandRegistration, selectSleeve
// Composable: getAdvisorProfilePic

// Local vars: isHouseholdSelected
</script>

<template>
  <UDashboardSidebar
    id="default"
    v-model:open="open"
    collapsible
    resizable
    class="border-none "
    :ui="{
      wrapper: 'bg-gradient-to-br from-[#E0E0D2] to-[#7A7A73]',
      header: 'px-0',
      body: 'no-scrollbar bg-transparent px-0 py-2',
      // This targets the mobile slide-over/drawer specifically
      container: 'bg-gradient-to-br from-[#E0E0D2] to-[#7A7A73]',
      content: 'bg-gradient-to-br from-[#E0E0D2] to-[#7A7A73]'
    }"
    :min-size="17"
    :default-size="20"
    :max-size="24"
  >
    <template #header="{ collapsed }">
      <!-- Padding lives on the slot content (not the sidebar :ui) so it can be
           collapsed-aware: the 64px collapsed rail can't afford the 28px (px-7)
           expanded gutter, so it drops to px-2 and the mark self-centers.
           h-full + items-center anchors the logo to the FULL header height
           (same --ui-header-height as the body navbar) so the two line up. -->
      <div class="flex h-full w-full items-center" :class="collapsed ? 'px-2 justify-center' : 'px-7'">
        <NavAzzadLogoInfo :collapsed="collapsed" />
      </div>
    </template>

    <template #default="{ collapsed }">
      <div v-if="selectedHousehold || tabSession.household" class="mt-2 lg:-mt-2" :class="collapsed ? 'px-2' : 'px-7'">
        <!-- Advisor Button -->
        <div @click="onAdvisorClick">
          <NavAdvisorButton :collapsed="collapsed" :rep-name="householdRepName" />
        </div>

        <!-- Household -->
        <div @click="onHouseholdClick" :class="collapsed ? 'flex justify-center' : ''">
          <NavHouseholdButton :collapsed="collapsed" :household-name="householdName" />
        </div>

        <div v-if="!collapsed">
          <!-- Registration And Sleeves. The skeleton mirrors the real row's
               silhouette, so the swap doesn't reflow the column. -->
          <NavRegsAndSleevesSkeleton v-if="loading" />
          <NavRegsAndSleevesList
            v-else
            :registrations="registrations"
            :sleeves="sleeves"
            @close-sidebar="open = false"
          />
        </div>
      </div>
    </template>
  </UDashboardSidebar>
</template>

<style scoped>
</style>
